import { Dispatch, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../api/users-api';
import { ChromeDeviceModel } from '../../../../types';

import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../shared/analytics/module-list';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';

interface ChromeDevicesPaginatedGridToolbarProps {
  selectedDevices: ChromeDeviceModel[];
  toolsRoles: string[];
  setOpenDisableDevices: Dispatch<SetStateAction<boolean>>;
  setOpenEnableDevices: Dispatch<SetStateAction<boolean>>;
  setOpenDeprovisionDevices: Dispatch<SetStateAction<boolean>>;
  setOpenMoveOrgUnitDialog: Dispatch<SetStateAction<boolean>>;
}

export default function ChromeDevicesPaginatedGridToolbar({
  selectedDevices,
  toolsRoles,
  setOpenDisableDevices,
  setOpenEnableDevices,
  setOpenDeprovisionDevices,
  setOpenMoveOrgUnitDialog,
}: ChromeDevicesPaginatedGridToolbarProps) {
  const queryClient = useQueryClient();

  const containsDeprovisionedDevices = selectedDevices.some(
    (device) => device.status === 'DEPROVISIONED'
  );
  const containsDisabledDevices = selectedDevices.some(
    (device) => device.status === 'DISABLED'
  );
  const containsEnabledDevices = selectedDevices.some(
    (device) => device.status === 'ACTIVE'
  );

  return (
    <SharedToolbar
      noQuickFilter
      secondaryButtons={
        <>
          <SharedIconButton
            variant="transfer"
            onClick={() => setOpenMoveOrgUnitDialog(true)}
            disabled={
              selectedDevices.length === 0 ||
              !isAbleToSeeUIElement(toolsRoles, ToolsRoles.CanMoveDevices.value)
            }
            tooltipLabel="Move"
          />
          <SharedIconButton
            variant="enable"
            onClick={() => setOpenEnableDevices(true)}
            disabled={
              selectedDevices.length === 0 ||
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.CanReenableDevices.value
              ) ||
              containsDeprovisionedDevices ||
              containsEnabledDevices
            }
            tooltipLabel={
              containsDeprovisionedDevices
                ? 'Cannot enable Deprovisioned devices'
                : containsEnabledDevices
                ? 'All selected devices must be disabled'
                : 'Enable'
            }
          />
          <SharedIconButton
            variant="block"
            onClick={() => setOpenDisableDevices(true)}
            disabled={
              selectedDevices.length === 0 ||
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.CanDisableDevices.value
              ) ||
              containsDeprovisionedDevices ||
              containsDisabledDevices
            }
            tooltipLabel={
              containsDeprovisionedDevices
                ? 'Cannot disable Deprovisioned devices'
                : containsDisabledDevices
                ? 'All selected devices must be enabled'
                : 'Disable'
            }
          />
          <SharedIconButton
            variant="disable"
            onClick={() => setOpenDeprovisionDevices(true)}
            disabled={
              selectedDevices.length === 0 ||
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.CanDeprovisionDevices.value
              ) ||
              containsDeprovisionedDevices
            }
            tooltipLabel={
              containsDeprovisionedDevices
                ? 'All selected devices are Deprovisioned'
                : 'Deprovision'
            }
          />
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.ChromeDevicesPage],
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="chromeDevicesGridStates"
            module={ModuleList.modules.devices.name}
            section={ModuleList.modules.devices.sections.chromeDevices}
          />
        </>
      }
    ></SharedToolbar>
  );
}
